import { TractorAssembler } from '@contracts/order/OrderForm';
import type { TractorWithAxlesResponse } from '@contracts/tractor/TractorWithAxlesResponse';
import { yupResolver } from '@hookform/resolvers/yup';
import { AxlesTable } from '@src/axles/AxlesTable';
import { Accordion } from '@src/common/components/Accordion';
import { CancelButton } from '@src/common/components/button/CancelButton';
import { SaveChangesButton } from '@src/common/components/button/SaveChangesButton';
import { Divider } from '@src/common/components/Divider';
import { Stack } from '@src/common/components/Stack';
import { AxleInformationRow } from '@src/orders/PermitFormSections/axlesConfiguration/AxleInformationRow';
import { ConditionalFieldsContext } from '@src/orders/PermitFormSections/ConditionalFieldsContext';
import { axleRowFields } from '@src/orders/schemas/AxleInformationInputsSchema';
import { AxleFormName } from '@src/permitField/AxleInformationFields';
import { TractorAxlesFormUtils } from '@src/permitField/TractorAxlesFormUtils';
import {
  TractorFloridaVehicleConfigurationField,
  tractorInformationFormField,
  TractorOntarioVehicleTypeField,
} from '@src/permitField/TractorInformationFields';
import { TractorDimensionsForm } from '@src/tractor/TractorDimensionsForm';
import { TractorFormInputs } from '@src/tractor/TractorFormInputs';
import { TractorInformationForm } from '@src/tractor/TractorInformationForm';
import { TractorLicenseForm } from '@src/tractor/TractorLicenseForm';
import { useState } from 'react';
import type { FieldError } from 'react-hook-form';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type * as yup from 'yup';

type AccordionState = {
  openInformation: boolean;
  openLicensePlateDetails: boolean;
  openDimensions: boolean;
  openAxles: boolean;
};

interface Props {
  prefillData?: TractorWithAxlesResponse;
  onSubmit: (data: yup.InferType<typeof TractorFormInputs>) => void;
  onCancel: () => void;
  isLoading: boolean;
}

export const TractorPageForm = ({
  prefillData,
  onSubmit,
  onCancel,
  isLoading,
}: Props) => {
  const [accordionState, setAccordionState] = useState<AccordionState>({
    openInformation: true,
    openLicensePlateDetails: true,
    openDimensions: true,
    openAxles: true,
  });
  const { t } = useTranslation();
  const methods = useForm({
    resolver: yupResolver(TractorFormInputs),
    defaultValues: getTractorValues(prefillData),
    context: {
      mode: prefillData ? 'updateTractor' : 'addTractor',
    },
  });
  const missingAxlesError =
    !!methods.formState.errors[TractorAxlesFormUtils.fieldName]?.[
      AxleFormName as keyof FieldError
    ];
  const tractorAxles = methods.watch(TractorAxlesFormUtils.pathToAxles);
  const axleCount = Array.isArray(tractorAxles) ? tractorAxles.length : 0;

  return (
    <FormProvider {...methods}>
      <ConditionalFieldsContext.Provider value={{ disabledFields: [] }}>
        <form
          onSubmit={(e) => {
            methods.handleSubmit(onSubmit)(e);
            e.stopPropagation();
          }}
        >
          <Stack>
            <Accordion
              title={t('tractorInformation')}
              id="tractor-information"
              expanded={accordionState.openInformation}
              onClick={(e) => {
                e.stopPropagation();
                setAccordionState((prev) => ({
                  ...prev,
                  openInformation: !prev.openInformation,
                }));
              }}
            >
              <TractorInformationForm
                control={methods.control}
                fields={Object.keys(tractorInformationFormField).filter(
                  (field) =>
                    ![
                      TractorFloridaVehicleConfigurationField.fieldName,
                      TractorOntarioVehicleTypeField.fieldName,
                    ].includes(field),
                )}
              />
            </Accordion>
            <Divider />
            <Accordion
              title={t('tractorLicenseDetails')}
              id="tractor-license-details"
              expanded={accordionState.openLicensePlateDetails}
              onClick={() =>
                setAccordionState((prev) => ({
                  ...prev,
                  openLicensePlateDetails: !prev.openLicensePlateDetails,
                }))
              }
            >
              <TractorLicenseForm control={methods.control} />
            </Accordion>
            <Divider />
            <Accordion
              title={t('tractorDimensions')}
              id="tractor-dimensions"
              expanded={accordionState.openDimensions}
              onClick={() =>
                setAccordionState((prev) => ({
                  ...prev,
                  openDimensions: !prev.openDimensions,
                }))
              }
            >
              <TractorDimensionsForm control={methods.control} />
            </Accordion>
            <Divider />
            <Accordion
              title={t('axleInformation')}
              id="tractor-axles"
              expanded={accordionState.openAxles}
              onClick={() =>
                setAccordionState((prev) => ({
                  ...prev,
                  openAxles: !prev.openAxles,
                }))
              }
            >
              <AxlesTable
                name={t('axleTable')}
                vehiclePrefix={TractorAxlesFormUtils.fieldName}
                axleCount={axleCount ?? 0}
                getValue={methods.getValues}
                setValue={methods.setValue}
                fields={Object.keys(axleRowFields)}
                missingAxlesError={missingAxlesError}
              >
                {Array.from({ length: axleCount ?? 0 }).map((_, i) => (
                  <AxleInformationRow
                    fields={Object.keys(axleRowFields)}
                    key={`axle-${i}-information`}
                    index={i}
                    isLoading={isLoading}
                    vehicleType="TRACTOR"
                    vehiclePrefix={TractorAxlesFormUtils.fieldName}
                    getValue={methods.getValues}
                    setValue={methods.setValue}
                    totalAxles={axleCount ?? 0}
                    cumulativeAxleCount={0}
                  />
                ))}
              </AxlesTable>
            </Accordion>
            <Stack direction="row" justifyContent="end" gap="10px">
              <CancelButton onClick={onCancel} />
              <SaveChangesButton disabled={isLoading} />
            </Stack>
          </Stack>
        </form>
      </ConditionalFieldsContext.Provider>
    </FormProvider>
  );
};

const getTractorValues = (tractor: TractorWithAxlesResponse | undefined) => {
  if (!tractor) return undefined;

  return TractorAssembler.toFormData(tractor);
};
